import * as Sentry from "@sentry/nextjs";
import { DateTime } from "luxon";

import { SHOP_TIMEZONE } from "../../config";
import { Locale } from "../lib/i18n";
import { CalendarRow, CalendarRowKey } from "../types/calendar";
import { Product } from "../types/shopify";
import { DynamicPricingCalendarScheduleDate } from "../types/shopify-components";

export type SpecialEvent = {
  [Key in Locale]: {
    title?: string;
    content?: string;
  };
};

interface ProductScheduleDate {
  date: string; // date
  /**
   * A human-readable definition of opening times. This may be e.g. a range or a list of start times.
   *
   * example:
   * 11, 15
   */
  times?: string;
  specialEvent?: SpecialEvent;
}

type ProductScheduleDateWithDateTime = Omit<ProductScheduleDate, "date"> & {
  date: DateTime;
};

export type ProductSchedule = ProductScheduleDate[];
export type ProductScheduleWithDateTimes = ProductScheduleDateWithDateTime[];

export enum DefinitionForDay {
  CLOSED = "Suljettu",
  OPEN = "Avoin",
}

export function getCurrentShopDate(): string {
  return DateTime.now().setZone(SHOP_TIMEZONE).toISODate();
}

function buildSchedule(
  calendarRows: CalendarRow[],
  name: CalendarRowKey,
  rangeStart: string,
  rangeEnd?: string,
): ProductSchedule {
  const ret: ProductSchedule = [];
  for (const row of calendarRows) {
    const date = row.Pvm;
    const definitionForDay = row[name]; // e.g. "15-17.30", "11.30, 13.30, 15", "Avoin", "Suljettu"
    if (
      definitionForDay !== undefined &&
      definitionForDay !== DefinitionForDay.CLOSED &&
      rangeStart <= date &&
      (!rangeEnd || rangeEnd >= date)
    ) {
      ret.push({
        date,
        times: definitionForDay,
        specialEvent: {
          fi: {
            title: row.Tiedote,
            content: row.Tiedoteteksti,
          },
          en: {
            title: row.Tiedote_ENG,
            content: row.Tiedoteteksti_ENG,
          },
        },
      });
    }
  }
  return ret;
}

function tryParseDate(tag: string, kind: "start" | "end"): string | undefined {
  const dateTokens = tag.split(`calendar_${kind}date_`);
  if (dateTokens.length === 2 && dateTokens[0] === "") {
    const datePart = dateTokens[1];
    if (datePart.length === 10 && !isNaN(new Date(datePart).getTime())) {
      return datePart;
    } else {
      console.warn(`Incorrect calendar ${kind} date tag: ${tag}`);
    }
  }
  return;
}

export function getProductSchedule(
  product: Product,
  calendarRows: CalendarRow[],
): ProductSchedule {
  let schedule: ProductSchedule = [];

  const currentShopDate = getCurrentShopDate();
  let calendarName: CalendarRowKey | undefined;
  const calendarRange: { start?: string; end?: string } = {};

  if (!product.tags) {
    return schedule;
  }

  for (const tag of product.tags) {
    const nameTokens = tag.split("calendar_name_");
    if (nameTokens.length === 2 && nameTokens[0] === "") {
      if (calendarName) {
        console.warn(
          `Product ${product.handle} has multiple calendar name tags, using first one`,
        );
      } else if (nameTokens[1].length > 0) {
        calendarName = nameTokens[1] as CalendarRowKey;
      } else {
        console.warn(
          `Product ${product.handle} has empty calendar name tag, ignoring`,
        );
      }
      continue;
    }
    const parsedStartDate = tryParseDate(tag, "start");
    if (parsedStartDate) {
      if (calendarRange.start) {
        console.warn(
          `Product ${product.handle} has multiple calendar start tags, using first one`,
        );
      } else {
        calendarRange.start = parsedStartDate;
      }
      continue;
    }
    const parsedEndDate = tryParseDate(tag, "end");
    if (parsedEndDate) {
      if (calendarRange.end) {
        console.warn(
          `Product ${product.handle} has multiple calendar end tags, using first one`,
        );
      } else {
        calendarRange.end = parsedEndDate;
      }
      continue;
    }
  }
  if ((calendarRange.start || calendarRange.end) && !calendarName) {
    const message = `Calendar dates defined for product ${product.handle} without a calendar name`;
    Sentry.captureMessage(message);
    console.warn(message);
  }
  if (calendarName) {
    schedule = buildSchedule(
      calendarRows,
      calendarName,
      !calendarRange.start || calendarRange.start < currentShopDate
        ? currentShopDate
        : calendarRange.start,
      calendarRange.end,
    );
    if (schedule.length === 0) {
      const message = `Product ${product.handle} is linked to calendar "${calendarName}, but could not get any opening times`;
      Sentry.captureMessage(message);
      console.warn(message);
    }
  }
  return schedule;
}

export function getStartingDate(
  schedule: DynamicPricingCalendarScheduleDate[],
): DateTime {
  const today = DateTime.now().startOf("day").setZone(SHOP_TIMEZONE);

  if (!schedule.length) {
    return today;
  }

  const earliestScheduleDate = schedule
    .reduce(
      (earliest, { date }) => (earliest > date ? date : earliest),
      schedule[0].date,
    )
    .startOf("day");

  return earliestScheduleDate >= today ? earliestScheduleDate : today;
}
